import { useEffect, useMemo } from 'react';

import { StructureAttribute } from '../../../@types';
import { CustomNodeAttributeType } from '../../../@types/service.type';
import { DropdownItem } from '../../../components/atoms/SelectMenu';
import { PrioritySelectItem } from '../../../components/molecules/PrioritySelectMenu';
import {
  FaultCategory,
  NodeAttributeType,
  PropertyType,
  ServiceTypeCode,
} from '../../../configs/enums';
import {
  useGetFilteredNodeAttributesByCustomRange,
  useGetNodeAttributesByCustomRange,
} from '../../../queries/structure-query';
import { getInitialPriority, priorityTypes } from '../../../utils/services';

const PRIORITY_ATTRS = [NodeAttributeType.PriorityAndServices];
const FAULT_ATTRS = [NodeAttributeType.FaultCategory, NodeAttributeType.FaultCode];
const MAPPING_ATTRS = [NodeAttributeType.ServiceTypeMappings];

const hasActivePriorities = (data: CustomNodeAttributeType[]): boolean =>
  data.some((a) => a.propertyTypeCode === PropertyType.PriorityAndService && a.value?.isActive);

export interface ClientAttributes {
  mappings: CustomNodeAttributeType[];
  mappingsLoading: boolean;
}

export const useRepairJobClientAttributes = (clientNodeId: string): ClientAttributes => {
  const query = useGetNodeAttributesByCustomRange(clientNodeId, MAPPING_ATTRS);
  const nodeAttributes: CustomNodeAttributeType[] = useMemo(() => query.data ?? [], [query.data]);

  const mappings = useMemo(
    () =>
      nodeAttributes.filter(
        (a) =>
          a.propertyTypeCode === PropertyType.ServiceTypeMappings &&
          a.value?.selectedServiceType === ServiceTypeCode.Repair
      ),
    [nodeAttributes]
  );

  return { mappings, mappingsLoading: query.isFetching };
};

const compareByLabel = (a: DropdownItem, b: DropdownItem): number => a.label.localeCompare(b.label);

export interface FaultGroup {
  mainFaultOptions: DropdownItem[];
  descriptionsByFault: Record<string, DropdownItem[]>;
  faultIdByDescriptionId: Record<string, string>;
  faultHasDescriptions: Record<string, boolean>;
}

export const buildFaultGroups = (raw: StructureAttribute[]): FaultGroup => {
  const mains = raw.filter(
    (a) => a.propertyTypeCode === PropertyType.FaultCategory && a.value?.faults?.isActive
  );
  const subs = raw.filter(
    (a) => a.propertyTypeCode === PropertyType.FaultCode && a.value?.isActive
  );

  const descriptionsByFault: Record<string, DropdownItem[]> = {};
  const faultIdByDescriptionId: Record<string, string> = {};
  const faultHasDescriptions: Record<string, boolean> = {};

  for (const main of mains) {
    const mainId = String(main.id);
    const childSubs = subs.filter((s) => s.value?.categoryId === main.id);
    faultHasDescriptions[mainId] = childSubs.length > 0;
    if (childSubs.length > 0) {
      descriptionsByFault[mainId] = childSubs
        .map((s) => ({ value: String(s.id), label: s.value?.name ?? '' }))
        .sort(compareByLabel);
      for (const sub of childSubs) {
        faultIdByDescriptionId[String(sub.id)] = mainId;
      }
    }
  }

  const mainFaultOptions: DropdownItem[] = mains
    .map((m) => ({ value: String(m.id), label: m.value?.faults?.name ?? '' }))
    .sort(compareByLabel);

  return { mainFaultOptions, descriptionsByFault, faultIdByDescriptionId, faultHasDescriptions };
};

export interface FaultAttributes extends FaultGroup {
  priorities: PrioritySelectItem[];
  initialPriorityCode: string;
  faultsLoading: boolean;
  hasNoFaults: boolean;
}

export const useRepairJobFaultOptions = (
  contractNodeId: string,
  clientNodeId: string,
  serviceTypeCode: string,
  actionTypeCode: string
): FaultAttributes => {
  // Use the contract node when present, otherwise fall back to the client node
  // so callers without a contract still get attributes resolved correctly.
  const primaryNodeId = contractNodeId && contractNodeId !== '0' ? contractNodeId : clientNodeId;

  const priorityQuery = useGetFilteredNodeAttributesByCustomRange(
    primaryNodeId,
    PRIORITY_ATTRS,
    serviceTypeCode,
    FaultCategory.Reported,
    actionTypeCode
  );

  // Mirror legacy CMS2 / create-service behaviour: when the contract-scoped
  // query returns no active priorities, fall back to the client-level config.
  // Only meaningful when contract and client nodes differ.
  const fallbackEnabled = !!clientNodeId && clientNodeId !== '0' && clientNodeId !== primaryNodeId;
  const clientPriorityQuery = useGetFilteredNodeAttributesByCustomRange(
    clientNodeId,
    PRIORITY_ATTRS,
    serviceTypeCode,
    FaultCategory.Reported,
    actionTypeCode
  );

  const faultQuery = useGetFilteredNodeAttributesByCustomRange(
    primaryNodeId,
    FAULT_ATTRS,
    serviceTypeCode,
    FaultCategory.Reported,
    actionTypeCode
  );

  useEffect(() => {
    if (primaryNodeId && primaryNodeId !== '0' && serviceTypeCode && actionTypeCode) {
      priorityQuery.refetch();
      faultQuery.refetch();
    }
  }, [primaryNodeId, serviceTypeCode, actionTypeCode]);

  const primaryPriorityData = priorityQuery.data as CustomNodeAttributeType[] | undefined;
  const primaryHasPriorities = primaryPriorityData
    ? hasActivePriorities(primaryPriorityData)
    : false;
  const primaryFetched = priorityQuery.isFetched;

  // Trigger the client-level fallback only after the primary call has resolved
  // empty — using isFetched (not data presence) so we don't fire prematurely.
  useEffect(() => {
    if (
      fallbackEnabled &&
      serviceTypeCode &&
      actionTypeCode &&
      primaryFetched &&
      !priorityQuery.isFetching &&
      !primaryHasPriorities
    ) {
      clientPriorityQuery.refetch();
    }
  }, [
    fallbackEnabled,
    serviceTypeCode,
    actionTypeCode,
    primaryFetched,
    primaryHasPriorities,
    priorityQuery.isFetching,
  ]);

  const priorityAttrs: CustomNodeAttributeType[] = useMemo(() => {
    const primary = primaryPriorityData ?? [];
    if (hasActivePriorities(primary)) return primary;
    if (!fallbackEnabled) return primary;
    return (clientPriorityQuery.data as CustomNodeAttributeType[] | undefined) ?? primary;
  }, [primaryPriorityData, clientPriorityQuery.data, fallbackEnabled]);

  const faultData: CustomNodeAttributeType[] = useMemo(
    () => faultQuery.data ?? [],
    [faultQuery.data]
  );
  const group = useMemo(() => buildFaultGroups(faultData), [faultData]);
  // priorityTypes() returns numeric ids in `value`; coerce to string so they line up with
  // PrioritySelectItem (string contract) and the string IDs produced by getInitialPriority/setPriorityCode.
  // Without this, MUI Select's strict-equality check treats the displayed value and the items as different,
  // and selection silently fails.
  const priorities = useMemo<PrioritySelectItem[]>(
    () =>
      priorityTypes(priorityAttrs).map((p: PrioritySelectItem) => ({
        ...p,
        value: String(p.value),
      })),
    [priorityAttrs]
  );
  const initialPriorityCode = useMemo(() => getInitialPriority(priorityAttrs), [priorityAttrs]);
  const hasNoFaults = !faultQuery.isFetching && group.mainFaultOptions.length === 0;

  return {
    ...group,
    priorities,
    initialPriorityCode,
    faultsLoading: faultQuery.isFetching,
    hasNoFaults,
  };
};
